import { useEffect } from "react";

export type LearningResourceType = 
  | "Quiz"
  | "Video" 
  | "Article"
  | "Exercise"
  | "Course"
  | "Lesson"
  | "Tutorial"
  | "LearningResource";

export interface LearningResourceSchemaProps {
  name: string;
  description: string;
  url: string;
  learningResourceType: LearningResourceType;
  educationalLevel?: "beginner" | "intermediate" | "advanced";
  educationalUse?: "assessment" | "instruction" | "practice";
  timeRequired?: string; // ISO 8601 duration (e.g., "PT15M" for 15 minutes)
  inLanguage?: string;
  isAccessibleForFree?: boolean;
  author?: string;
  publisher?: string;
  publisherUrl?: string;
  datePublished?: string;
  dateModified?: string;
  keywords?: string[];
  teaches?: string[];
  assesses?: string[];
  thumbnail?: string;
  interactivityType?: "active" | "expositive" | "mixed";
  numberOfQuestions?: number;
}

/**
 * LearningResourceSchema component for quizzes, videos, and educational content
 * Generates Schema.org LearningResource markup optimized for Google Search
 */
export function LearningResourceSchema({
  name,
  description,
  url,
  learningResourceType,
  educationalLevel = "intermediate",
  educationalUse = "practice",
  timeRequired,
  inLanguage = "pt-BR",
  isAccessibleForFree = false,
  author = "Data Hire Club",
  publisher = "Data Hire Club",
  publisherUrl = "https://datahireclub.com.br",
  datePublished,
  dateModified,
  keywords = [],
  teaches = [],
  assesses = [],
  thumbnail,
  interactivityType = "active",
  numberOfQuestions
}: LearningResourceSchemaProps) {
  useEffect(() => {
    const schemaId = `learning-resource-${name.replace(/\s+/g, '-').toLowerCase()}`;
    
    // Remove existing schema
    const existingSchema = document.getElementById(schemaId);
    if (existingSchema) {
      existingSchema.remove();
    }

    const schema: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": learningResourceType === "Quiz" ? ["LearningResource", "Quiz"] : "LearningResource",
      "name": name,
      "description": description,
      "url": url,
      "learningResourceType": learningResourceType,
      "educationalLevel": educationalLevel,
      "educationalUse": educationalUse,
      "inLanguage": inLanguage,
      "isAccessibleForFree": isAccessibleForFree,
      "interactivityType": interactivityType,
      "author": {
        "@type": "Organization",
        "name": author
      },
      "publisher": {
        "@type": "Organization",
        "name": publisher,
        "url": publisherUrl
      }
    };

    if (timeRequired) {
      schema.timeRequired = timeRequired;
    }

    if (datePublished) {
      schema.datePublished = datePublished;
    }

    if (dateModified) {
      schema.dateModified = dateModified;
    }

    if (keywords.length > 0) {
      schema.keywords = keywords.join(", ");
    }

    if (teaches.length > 0) {
      schema.teaches = teaches.map(skill => ({
        "@type": "DefinedTerm",
        "name": skill
      }));
    }

    if (assesses.length > 0) {
      schema.assesses = assesses.map(skill => ({
        "@type": "DefinedTerm",
        "name": skill
      }));
    }

    if (thumbnail) {
      schema.thumbnailUrl = thumbnail;
      schema.image = thumbnail;
    }

    // Quiz-specific properties
    if (learningResourceType === "Quiz" && numberOfQuestions) {
      schema.numberOfItems = numberOfQuestions;
    }

    // Video-specific properties
    if (learningResourceType === "Video") {
      schema["@type"] = ["LearningResource", "VideoObject"];
    }

    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById(schemaId);
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [name, description, url, learningResourceType, educationalLevel, educationalUse,
      timeRequired, inLanguage, isAccessibleForFree, author, publisher, publisherUrl,
      datePublished, dateModified, keywords, teaches, assesses, thumbnail, 
      interactivityType, numberOfQuestions]);

  return null;
}

export default LearningResourceSchema;
